// Clientes Contratos - Lista de contratos del cliente
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClientLayout } from '@/components/Layout/ClientLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import { useAuth } from '@/providers/AuthProvider';
import { clienteService } from '@/services/clienteService';
import { contractsService } from '@/services/contractsService';
import { formatCurrency, formatDate } from '@/utils/formatters';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import type { ClienteContractResponse } from '@/types/contracts';
import { CONTRACT_STATUS_COLORS } from '@/types/contracts';
import { CONTRACT_TYPE_LABELS, type ContractType } from '@/types/admin';

export function ClientesContratos() {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const { user, isAuthenticated } = useAuth();
  const [contracts, setContracts] = useState<ClienteContractResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadContracts = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log('📋 [ClientesContratos] Cargando contratos para cliente:', user.email);

      // Usar el nuevo endpoint de clientes
      const clientContracts = await clienteService.getContratos();
      setContracts(clientContracts);
      console.log('✅ [ClientesContratos] Contratos cargados:', clientContracts.length);
    } catch (err: unknown) {
      console.error('❌ [ClientesContratos] Error cargando contratos:', err);
      setContracts([]);
      setError(t('contracts.loadError'));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, t]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  const getStatusBadge = (status: string) => {
    const colors = CONTRACT_STATUS_COLORS[status as keyof typeof CONTRACT_STATUS_COLORS] || 'bg-gray-100 text-gray-800';
    const labels: Record<string, string> = {
      pending: t('contracts.statusPending'),
      paid: t('contracts.statusPaid'),
      completed: t('contracts.statusCompleted'),
      expired: t('contracts.statusExpired'),
      cancelled: t('contracts.statusCancelled'),
    };
    return <Badge className={colors}>{labels[status]}</Badge>;
  };

  const getContractTypeLabel = (contractType?: ContractType) => {
    if (!contractType) return null;
    return CONTRACT_TYPE_LABELS[contractType] ?? contractType;
  };

  const handleDownloadContract = async (code: string, isFinal: boolean = false) => {
    try {
      await contractsService.downloadContractFile(
        code,
        `contrato-${code}${isFinal ? '-final' : ''}.pdf`,
        isFinal
      );
    } catch (err: unknown) {
      console.error('Error descargando contrato:', err);
      alert(t('contracts.downloadError'));
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" text={t('contracts.loadingText')} />
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
      <Card className="border-yellow-200 bg-yellow-50">
        <CardContent className="pt-6 space-y-4">
          <div className="text-sm text-yellow-800">{error}</div>
          <Button onClick={() => loadContracts()} variant="outline" size="sm">
            {t('common.retry')}
          </Button>
        </CardContent>
      </Card>
      </ClientLayout>
    );
  }

  if (contracts.length === 0) {
    return (
      <ClientLayout>
      <EmptyState
        icon={<DocumentTextIcon width={48} height={48} className="text-gray-400" />}
        title={t('contracts.noContracts')}
        description={t('contracts.noContractsDesc')}
      />
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('contracts.title')}</h1>
        <p className="text-sm text-gray-600 mt-1">
          {t('contracts.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {contracts.map((contract) => (
          <Card key={contract.hiring_code} className="shadow-sm">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <DocumentTextIcon width={20} height={20} />
                    {t('contracts.contract', { code: contract.hiring_code })}
                  </CardTitle>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {getStatusBadge(contract.status)}
                    {contract.contract_type && (
                      <Badge variant="outline">{getContractTypeLabel(contract.contract_type)}</Badge>
                    )}
                    {contract.contract_type === 'linked' && (
                      <Badge className="bg-amber-100 text-amber-800">{t('contracts.offer2x1')}</Badge>
                    )}
                    {contract.service_name && (
                      <Badge variant="outline">{contract.service_name}</Badge>
                    )}
                  </div>
                  {contract.contract_type === 'linked' && (contract.linked_partner_name || contract.linked_contract_code) && (
                    <p className="text-xs text-gray-500 mt-2">
                      {contract.linked_partner_name
                        ? t('contracts.linkedContractWith', { name: contract.linked_partner_name })
                        : t('contracts.linkedContract')}
                      {contract.linked_contract_code && t('contracts.linkedContractCode', { code: contract.linked_contract_code })}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => navigate(`/clientes/${contract.hiring_code}`)}
                    variant="outline"
                    size="sm"
                  >
                    {t('common.viewDetails')}
                  </Button>
                  <Button
                    onClick={() => handleDownloadContract(contract.hiring_code, false)}
                    variant="outline"
                    size="sm"
                  >
                    <ArrowDownTrayIcon width={16} height={16} className="mr-2" />
                    {t('common.download')}
                  </Button>
                  {contract.status === 'completed' && (
                    <Button
                      onClick={() => handleDownloadContract(contract.hiring_code, true)}
                      variant="outline"
                      size="sm"
                    >
                      <ArrowDownTrayIcon width={16} height={16} className="mr-2" />
                      {t('common.final')}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-gray-500">{t('contracts.amount')}</label>
                  <div className="text-gray-900 font-semibold mt-1">
                    {formatCurrency(contract.amount, contract.currency, false)}
                  </div>
                </div>
                <div>
                  <label className="text-gray-500">{t('contracts.createdAt')}</label>
                  <div className="text-gray-900 mt-1">{formatDate(contract.created_at)}</div>
                </div>
                {contract.contract_accepted_at && (
                  <div>
                    <label className="text-gray-500">{t('contracts.acceptedAt')}</label>
                    <div className="text-gray-900 mt-1">{formatDate(contract.contract_accepted_at)}</div>
                  </div>
                )}
                {contract.expires_at && (
                  <div>
                    <label className="text-gray-500">{t('contracts.expiresAt')}</label>
                    <div className="text-gray-900 mt-1">{formatDate(contract.expires_at)}</div>
                  </div>
                )}
              </div>
              {(contract.holders && contract.holders.length > 1) && (
                <div className="pt-2 border-t border-gray-100">
                  <label className="text-gray-500 text-sm">{t('contracts.holders')}</label>
                  <ul className="text-sm text-gray-700 mt-1 list-disc list-inside">
                    {contract.holders.map((h, i) => (
                      <li key={i}>{h.full_name}{h.is_primary ? ` ${t('contracts.holderPrimary')}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(contract.dependents && contract.dependents.length > 0) && (
                <div className="pt-2 border-t border-gray-100">
                  <label className="text-gray-500 text-sm">{t('contracts.dependents')}</label>
                  <ul className="text-sm text-gray-700 mt-1 list-disc list-inside">
                    {contract.dependents.map((d, i) => (
                      <li key={i}>{d.full_name} ({d.relationship})</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
    </ClientLayout>
  );
}
